import React, { useEffect, useState } from 'react';
import { AppNav } from '../../components/layout/AppNav';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { DataTable, type Column } from '../../components/ui/DataTable';
import { Modal } from '../../components/ui/Modal';
import { FormField } from '../../components/ui/FormField';
import api from '../../services/api';
import { useToast } from '../../components/ui/Toast';
import { Plus } from 'lucide-react';

interface User {
  id: string;
  name: string;
  mobile: string;
  role: string;
  status: string;
  resellerId?: string;
}

export const ResellerManagementPage: React.FC = () => {
  const { showToast } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', mobile: '', role: 'reseller-agent' });

  const loadUsers = () => {
    setLoading(true);
    api.get<User[]>('/users')
      .then((r) => setUsers(r.data))
      .catch(() => showToast('Failed to load users', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(loadUsers, []);

  const handleToggle = async (user: User) => {
    try {
      const action = user.status === 'active' ? 'deactivate' : 'activate';
      await api.patch(`/users/${user.id}/${action}`);
      loadUsers();
    } catch {
      showToast('Failed to update user status', 'error');
    }
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      await api.post('/users', form);
      setShowModal(false);
      setForm({ name: '', mobile: '', role: 'reseller-agent' });
      loadUsers();
      showToast('User created successfully', 'success');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      showToast(msg ?? 'Failed to create user', 'error');
    } finally {
      setSaving(false);
    }
  };

  const columns: Column<User>[] = [
    { key: 'name', header: 'Name' },
    { key: 'mobile', header: 'Mobile', width: '160px' },
    { key: 'role', header: 'Role', width: '140px', render: (row) => <Badge label={row.role} /> },
    { key: 'status', header: 'Status', width: '100px', render: (row) => <Badge label={row.status === 'active' ? 'Active' : 'Inactive'} /> },
    {
      key: 'actions', header: '', width: '120px',
      render: (row) => (
        <Button size="sm" variant={row.status === 'active' ? 'danger' : 'secondary'}
          onClick={(e) => { e.stopPropagation(); handleToggle(row); }}>
          {row.status === 'active' ? 'Deactivate' : 'Activate'}
        </Button>
      ),
    },
  ];

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <AppNav title="Reseller Management" subtitle="Manage agents and resellers" />
      <main style={{ flex: 1, padding: '24px', overflowY: 'auto' }}>

        <div className="page-header">
          <div className="page-title">Reseller Management</div>
          <button className="btn-primary" onClick={() => setShowModal(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Plus size={15} />
            Add User
          </button>
        </div>

        <div className="card">
          <DataTable columns={columns} data={users} keyField="id" loading={loading} emptyMessage="No users found." />
        </div>
      </main>

      <Modal open={showModal} onClose={() => setShowModal(false)} title="Add User" size="sm">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <FormField label="Name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <FormField label="Mobile" required placeholder="+9665XXXXXXXX" value={form.mobile} onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))} />
          <div>
            <label className="lbl">Role</label>
            <select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} className="inp">
              <option value="reseller-agent">Agent</option>
              <option value="reseller-admin">Admin</option>
            </select>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '4px' }}>
            <Button variant="ghost" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleCreate} loading={saving}>Create</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
